import { FormGroup } from '@angular/forms';
import {
  RfaFormControl,
  RfaValidators,
  RfaComponents,
  RfaTransformers,
} from '@innogy/feh/rfa';

import type {
  LeadFormControls,
  LeadFormRendering,
} from './lead-form.interface';
import { fieldToValue } from '../utils/field-to-value';
import { LeadFormDefaults } from './lead-form-defaults';

export function getLeadFormControls(
  rendering: LeadFormRendering,
  analyticsComponentName: string = rendering.componentName
): LeadFormControls {
  return {
    title: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.TitleLabel,
          LeadFormDefaults.fields.TitleLabel
        ),
        groupName: 'consumerTitle',
        dataAutomationId: 'lead-form--salutation',
        validators: [RfaValidators.required],
        helpText: fieldToValue(rendering.fields.TitleHintText),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.TitleRequiredError,
              LeadFormDefaults.fields.TitleRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
        },
        entries: [
          { text: 'De heer', value: 'Heer' },
          { text: 'Mevrouw', value: 'Mevrouw' },
          { text: 'Geen van beide', value: '-' },
        ],
        analytics: {
          valueChangeTracker: {
            componentName: analyticsComponentName,
          },
        },
      },
      RfaComponents.RadioGroup
    ),
    name: new FormGroup({
      initials: new RfaFormControl(
        '',
        {
          label: fieldToValue(
            rendering.fields.InitialsLabel,
            LeadFormDefaults.fields.InitialsLabel
          ),
          autocompleteId: 'initials',
          dataAutomationId: 'lead-form--name-initials',
          transformers: [RfaTransformers.initials],
          validators: [RfaValidators.required],
          helpText: fieldToValue(rendering.fields.InitialsHintText),
          errorMsgs: {
            required: {
              msg: fieldToValue(
                rendering.fields.InitialsRequiredError,
                LeadFormDefaults.fields.InitialsRequiredError
              ),
              analytics: {
                componentName: analyticsComponentName,
              },
            },
          },
          analytics: {
            focusChangeTracker: {
              componentName: analyticsComponentName,
            },
          },
        },
        RfaComponents.TextInput
      ),
      insertion: new RfaFormControl(
        '',
        {
          label: fieldToValue(
            rendering.fields.InsertionsLabel,
            LeadFormDefaults.fields.InsertionsLabel
          ),
          dataAutomationId: 'lead-form--name-insertion',
          helpText: fieldToValue(rendering.fields.InsertionsHintText),
          analytics: {
            focusChangeTracker: {
              componentName: analyticsComponentName,
            },
          },
        },
        RfaComponents.TextInput
      ),
      lastName: new RfaFormControl(
        '',
        {
          label: fieldToValue(
            rendering.fields.LastNameLabel,
            LeadFormDefaults.fields.LastNameLabel
          ),
          autocompleteId: 'family-name',
          dataAutomationId: 'lead-form--name-lastname',
          transformers: [RfaTransformers.charLimit(100)],
          validators: [RfaValidators.required, RfaValidators.lastName],
          helpText: fieldToValue(rendering.fields.LastNameHintText),
          errorMsgs: {
            required: {
              msg: fieldToValue(
                rendering.fields.LastNameRequiredError,
                LeadFormDefaults.fields.LastNameRequiredError
              ),
              analytics: {
                componentName: analyticsComponentName,
              },
            },
          },
          analytics: {
            focusChangeTracker: {
              componentName: analyticsComponentName,
            },
          },
        },
        RfaComponents.TextInput
      ),
    }),
    email: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.EmailLabel,
          LeadFormDefaults.fields.EmailLabel
        ),
        inputType: 'email',
        autocompleteId: 'email',
        dataAutomationId: 'lead-form--email',
        transformers: [RfaTransformers.charLimit(100)],
        validators: [RfaValidators.required, RfaValidators.email],
        helpText: fieldToValue(rendering.fields.EmailHintText),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.EmailRequiredError,
              LeadFormDefaults.fields.EmailRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
          email: {
            msg: fieldToValue(
              rendering.fields.EmailInvalidError,
              LeadFormDefaults.fields.EmailInvalidError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
        },
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
          },
        },
      },
      RfaComponents.TextInput
    ),
    phone: new RfaFormControl(
      '',
      {
        label: fieldToValue(
          rendering.fields.PhoneLabel,
          LeadFormDefaults.fields.PhoneLabel
        ),
        inputType: 'tel',
        autocompleteId: 'phone',
        dataAutomationId: 'lead-form--phone',
        transformers: [RfaTransformers.charLimit(100)],
        validators: [RfaValidators.required, RfaValidators.phone],
        helpText: fieldToValue(rendering.fields.PhoneHintText),
        errorMsgs: {
          required: {
            msg: fieldToValue(
              rendering.fields.PhoneRequiredError,
              LeadFormDefaults.fields.PhoneRequiredError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
          phone: {
            msg: fieldToValue(
              rendering.fields.PhoneInvalidError,
              LeadFormDefaults.fields.PhoneInvalidError
            ),
            analytics: {
              componentName: analyticsComponentName,
            },
          },
        },
        analytics: {
          focusChangeTracker: {
            componentName: analyticsComponentName,
          },
        },
      },
      RfaComponents.TextInput
    ),
  };
}

export function createLeadForm(
  rendering: LeadFormRendering
): FormGroup<LeadFormControls> {
  return new FormGroup(getLeadFormControls(rendering));
}
